import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import {useTimeAllocationCategoriesStore} from '../stores/timeAllocationCategories.store.ts';
import {isDark, darkKey} from './useDark.js';

/**
 * Composable for managing time allocation category colors
 * Provides consistent color mapping across components with dark mode support
 */
export function useTimeAllocationColors() {
  const store = useTimeAllocationCategoriesStore();

  /**
   * Get color for a specific category key, respecting current theme
   * @param categoryKey - The category key (e.g., 'feature', 'sustain')
   * @returns The hex color code for the category
   */
  function getCategoryColor(categoryKey: string): string {
    // Access darkKey to trigger reactivity when theme changes
    darkKey.value;
    
    const category = store.categories.find(c => c.key === categoryKey);
    if (!category) return '#6B7280';
    
    // Use dark color if in dark mode and dark color exists
    if (isDark() && category.colorDark) {
      return category.colorDark;
    }
    
    return category.color;
  }

  /**
   * Get reactive computed color that updates with theme changes
   * @param categoryKey - The category key
   * @returns Computed ref that updates when theme changes
   */
  function getCategoryColorReactive(categoryKey: string) {
    return computed(() => {
      darkKey.value; // Trigger reactivity
      return getCategoryColor(categoryKey);
    });
  }

  /**
   * Get all categories with their associated colors (theme-aware)
   * @returns Array of categories with color property added
   */
  function getCategoriesWithColors() {
    // Access darkKey to trigger reactivity
    darkKey.value;
    
    return store.active.map(category => ({
      ...category,
      color: getCategoryColor(category.key)
    }));
  }

  /**
   * Get color mapping object (theme-aware)
   * @returns Object mapping category keys to colors
   */
  function getColorMapping() {
    // Access darkKey to trigger reactivity
    darkKey.value;
    
    const mapping: Record<string, string> = {};
    store.categories.forEach(category => {
      mapping[category.key] = getCategoryColor(category.key);
    });
    return mapping;
  }

  return {
    getCategoryColor,
    getCategoryColorReactive,
    getCategoriesWithColors,
    getColorMapping
  };
}
